import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useData } from '../../contexts/DataContext';

const { FiBarChart3, FiClock, FiDollarSign, FiDownload, FiCalendar, FiUser, FiBriefcase, FiFilter } = FiIcons;

const Reports = () => {
  const { tasks, projects, contacts } = useData();
  const [dateRange, setDateRange] = useState({
    startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 30 days ago
    endDate: new Date().toISOString().split('T')[0]
  });
  const [selectedProject, setSelectedProject] = useState('All');
  const [selectedAssignee, setSelectedAssignee] = useState('All');
  const [hourlyRate, setHourlyRate] = useState(75);

  const filteredTasks = useMemo(() => {
    return tasks.filter(task => {
      const taskDate = task.createdAt || new Date();
      const isInDateRange = taskDate >= new Date(dateRange.startDate) && taskDate <= new Date(dateRange.endDate);
      const matchesProject = selectedProject === 'All' || task.project === selectedProject;
      const matchesAssignee = selectedAssignee === 'All' || task.assignee === selectedAssignee;
      
      return isInDateRange && matchesProject && matchesAssignee && task.timeSpent > 0;
    });
  }, [tasks, dateRange, selectedProject, selectedAssignee]);

  const timeReports = useMemo(() => {
    const projectSummary = {};
    const assigneeSummary = {};
    let totalTime = 0;

    filteredTasks.forEach(task => {
      const projectName = task.project || 'Unassigned';
      const assigneeName = task.assignee || 'Unassigned';
      
      // Project summary
      if (!projectSummary[projectName]) {
        projectSummary[projectName] = {
          totalTime: 0,
          tasks: [],
          totalValue: 0
        };
      }
      projectSummary[projectName].totalTime += task.timeSpent;
      projectSummary[projectName].tasks.push(task);
      projectSummary[projectName].totalValue += (task.timeSpent / 3600) * hourlyRate;

      // Assignee summary
      if (!assigneeSummary[assigneeName]) {
        assigneeSummary[assigneeName] = {
          totalTime: 0,
          tasks: [],
          totalValue: 0
        };
      }
      assigneeSummary[assigneeName].totalTime += task.timeSpent;
      assigneeSummary[assigneeName].tasks.push(task);
      assigneeSummary[assigneeName].totalValue += (task.timeSpent / 3600) * hourlyRate;

      totalTime += task.timeSpent;
    });

    return {
      projectSummary,
      assigneeSummary,
      totalTime,
      totalValue: (totalTime / 3600) * hourlyRate,
      taskCount: filteredTasks.length
    };
  }, [filteredTasks, hourlyRate]);

  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours}h ${minutes}m`;
  };

  const formatDecimalTime = (seconds) => {
    return (seconds / 3600).toFixed(2);
  };

  const exportReport = () => {
    const reportData = {
      dateRange,
      summary: {
        totalTime: formatTime(timeReports.totalTime),
        totalValue: timeReports.totalValue.toFixed(2),
        taskCount: timeReports.taskCount,
        hourlyRate
      },
      projectBreakdown: Object.entries(timeReports.projectSummary).map(([project, data]) => ({
        project,
        time: formatTime(data.totalTime),
        decimalTime: formatDecimalTime(data.totalTime),
        value: data.totalValue.toFixed(2),
        taskCount: data.tasks.length
      })),
      taskDetails: filteredTasks.map(task => ({
        title: task.title,
        project: task.project,
        assignee: task.assignee,
        time: formatTime(task.timeSpent),
        decimalTime: formatDecimalTime(task.timeSpent),
        value: ((task.timeSpent / 3600) * hourlyRate).toFixed(2),
        date: task.createdAt?.toLocaleDateString()
      }))
    };

    const dataStr = JSON.stringify(reportData, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
    
    const exportFileDefaultName = `time-report-${dateRange.startDate}-to-${dateRange.endDate}.json`;
    
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
  };

  const uniqueAssignees = [...new Set(tasks.map(task => task.assignee).filter(Boolean))];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="space-y-6"
    >
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="text-3xl font-bold text-gray-800">Time Reports</h1>
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={exportReport}
          className="bg-green-600 text-white px-4 py-2 rounded-lg flex items-center space-x-2 hover:bg-green-700 transition-colors"
        >
          <SafeIcon icon={FiDownload} />
          <span>Export Report</span>
        </motion.button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">
          <SafeIcon icon={FiFilter} className="inline mr-2" />
          Report Filters
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Start Date
            </label>
            <input
              type="date"
              value={dateRange.startDate}
              onChange={(e) => setDateRange(prev => ({ ...prev, startDate: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              End Date
            </label>
            <input
              type="date"
              value={dateRange.endDate}
              onChange={(e) => setDateRange(prev => ({ ...prev, endDate: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Project
            </label>
            <select
              value={selectedProject}
              onChange={(e) => setSelectedProject(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="All">All Projects</option>
              {projects.map(project => (
                <option key={project.id} value={project.name}>{project.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Assignee
            </label>
            <select
              value={selectedAssignee}
              onChange={(e) => setSelectedAssignee(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="All">All Assignees</option>
              {uniqueAssignees.map(assignee => (
                <option key={assignee} value={assignee}>{assignee}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Hourly Rate ($)
            </label>
            <input
              type="number"
              value={hourlyRate}
              onChange={(e) => setHourlyRate(parseFloat(e.target.value) || 0)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              min="0"
              step="0.01"
            />
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Time</p>
              <p className="text-2xl font-bold text-gray-800 mt-1">
                {formatTime(timeReports.totalTime)}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                {formatDecimalTime(timeReports.totalTime)} hours
              </p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <SafeIcon icon={FiClock} className="text-blue-600 text-xl" />
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Value</p>
              <p className="text-2xl font-bold text-gray-800 mt-1">
                ${timeReports.totalValue.toFixed(2)}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                @ ${hourlyRate}/hour
              </p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <SafeIcon icon={FiDollarSign} className="text-green-600 text-xl" />
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Tasks</p>
              <p className="text-2xl font-bold text-gray-800 mt-1">
                {timeReports.taskCount}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                with time tracked
              </p>
            </div>
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
              <SafeIcon icon={FiBarChart3} className="text-purple-600 text-xl" />
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Avg. Rate</p>
              <p className="text-2xl font-bold text-gray-800 mt-1">
                ${timeReports.totalTime > 0 ? (timeReports.totalValue / (timeReports.totalTime / 3600)).toFixed(0) : 0}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                per hour
              </p>
            </div>
            <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
              <SafeIcon icon={FiDollarSign} className="text-orange-600 text-xl" />
            </div>
          </div>
        </motion.div>
      </div>

      {/* Project Breakdown */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">
          <SafeIcon icon={FiBriefcase} className="inline mr-2" />
          Time by Project
        </h3>
        <div className="space-y-4">
          {Object.entries(timeReports.projectSummary).map(([project, data]) => (
            <div key={project} className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-semibold text-gray-800">{project}</h4>
                <div className="text-right">
                  <div className="text-lg font-bold text-gray-800">
                    {formatTime(data.totalTime)}
                  </div>
                  <div className="text-sm text-green-600 font-semibold">
                    ${data.totalValue.toFixed(2)}
                  </div>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-600">
                <div>
                  <span className="font-medium">Tasks:</span> {data.tasks.length}
                </div>
                <div>
                  <span className="font-medium">Decimal Hours:</span> {formatDecimalTime(data.totalTime)}
                </div>
                <div>
                  <span className="font-medium">Avg per Task:</span> {data.tasks.length > 0 ? formatTime(data.totalTime / data.tasks.length) : '0h 0m'}
                </div>
              </div>

              {/* Progress bar */}
              <div className="mt-3">
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-blue-500 h-2 rounded-full"
                    style={{ 
                      width: `${timeReports.totalTime > 0 ? (data.totalTime / timeReports.totalTime) * 100 : 0}%` 
                    }}
                  />
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  {timeReports.totalTime > 0 ? ((data.totalTime / timeReports.totalTime) * 100).toFixed(1) : 0}% of total time
                </div>
              </div>
            </div>
          ))}
        </div>

        {Object.keys(timeReports.projectSummary).length === 0 && (
          <div className="text-center py-8">
            <SafeIcon icon={FiBriefcase} className="text-4xl text-gray-300 mx-auto mb-2" />
            <p className="text-gray-500">No time data found for the selected filters.</p>
          </div>
        )}
      </div>

      {/* Assignee Breakdown */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">
          <SafeIcon icon={FiUser} className="inline mr-2" />
          Time by Assignee
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {Object.entries(timeReports.assigneeSummary).map(([assignee, data]) => (
            <div key={assignee} className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-semibold text-gray-800">{assignee}</h4>
                <div className="text-right">
                  <div className="text-lg font-bold text-gray-800">
                    {formatTime(data.totalTime)}
                  </div>
                  <div className="text-sm text-green-600 font-semibold">
                    ${data.totalValue.toFixed(2)}
                  </div>
                </div>
              </div>
              
              <div className="space-y-1 text-sm text-gray-600">
                <div className="flex justify-between">
                  <span>Tasks:</span>
                  <span>{data.tasks.length}</span>
                </div>
                <div className="flex justify-between">
                  <span>Decimal Hours:</span>
                  <span>{formatDecimalTime(data.totalTime)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Avg per Task:</span>
                  <span>{data.tasks.length > 0 ? formatTime(data.totalTime / data.tasks.length) : '0h 0m'}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Detailed Task List */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">
          Task Details
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-3 px-2 font-semibold text-gray-700">Task</th>
                <th className="text-left py-3 px-2 font-semibold text-gray-700">Project</th>
                <th className="text-left py-3 px-2 font-semibold text-gray-700">Assignee</th>
                <th className="text-right py-3 px-2 font-semibold text-gray-700">Time</th>
                <th className="text-right py-3 px-2 font-semibold text-gray-700">Hours</th>
                <th className="text-right py-3 px-2 font-semibold text-gray-700">Value</th>
              </tr>
            </thead>
            <tbody>
              {filteredTasks.map((task) => (
                <tr key={task.id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="py-3 px-2 font-medium text-gray-800">{task.title}</td>
                  <td className="py-3 px-2 text-gray-600">{task.project || 'Unassigned'}</td>
                  <td className="py-3 px-2 text-gray-600">{task.assignee || 'Unassigned'}</td>
                  <td className="py-3 px-2 text-right font-mono">{formatTime(task.timeSpent)}</td>
                  <td className="py-3 px-2 text-right font-mono">{formatDecimalTime(task.timeSpent)}</td>
                  <td className="py-3 px-2 text-right font-semibold text-green-600">
                    ${((task.timeSpent / 3600) * hourlyRate).toFixed(2)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          
          {filteredTasks.length === 0 && (
            <div className="text-center py-8">
              <SafeIcon icon={FiClock} className="text-4xl text-gray-300 mx-auto mb-2" />
              <p className="text-gray-500">No tracked time found for the selected filters.</p>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default Reports;